/**
 * S14 tile-menu entries exporting the tile's LAST SUCCESSFUL result set
 * (CSV/XLSX/JSON via the shared G12 export pipeline, plus copy-as-CSV).
 * Items disable with a WHY tooltip until the tile has run once — the
 * tooltip lives on a wrapper span because Radix strips pointer events
 * from disabled items.
 */
import type { ReactNode } from 'react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { exportResults } from '../../../common/services/resultsExportApi';
import { resultSetToCsv } from '../../../common/utils/clipboardFormatters';
import { writeClipboardText } from '@/lib/clipboard';
import { tileRuntimeKey, useDashboardRuntimeStore } from '../../store/dashboardRuntimeStore';
import type { QueryBackedTile } from '../../services/tileExecutionService';

const NO_DATA_WHY = 'Run the tile first — there are no results to export';

const Item = ({
  why,
  onClick,
  testId,
  children,
}: {
  why?: string;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) => (
  <span title={why}>
    <DropdownMenuItem disabled={!!why} onClick={onClick} data-testid={testId}>
      {children}
    </DropdownMenuItem>
  </span>
);

export function TileDataExportMenuItems({
  dashboardId,
  tile,
}: {
  dashboardId: string;
  tile: QueryBackedTile;
}) {
  const { track } = useAnalytics();
  const lastSuccess = useDashboardRuntimeStore(
    (s) => s.byTile[tileRuntimeKey(dashboardId, tile.id)]?.lastSuccess,
  );
  const why = lastSuccess ? undefined : NO_DATA_WHY;

  const exportAs = (format: 'csv' | 'xlsx' | 'json') => async () => {
    if (!lastSuccess) return;
    const result = await exportResults({
      format,
      resultSets: [lastSuccess.result.selected],
      suggestedFileName: `${tile.title}.${format}`,
      engineId: tile.engine,
    });
    if (!result.success && !result.cancelled) {
      toast.error({ title: 'Export failed', description: result.error });
    }
  };

  const copyCsv = async () => {
    if (!lastSuccess) return;
    try {
      await writeClipboardText(resultSetToCsv(lastSuccess.result.selected));
      track('feature:dashboard_tile_copy');
    } catch {
      toast.error({ title: 'Copy failed', description: 'Could not write to the clipboard' });
    }
  };

  return (
    <>
      <DropdownMenuSeparator />
      <Item why={why} onClick={() => void exportAs('csv')()} testId="tile-export-csv-item">
        Export data as CSV…
      </Item>
      <Item why={why} onClick={() => void exportAs('xlsx')()} testId="tile-export-xlsx-item">
        Export data as XLSX…
      </Item>
      <Item why={why} onClick={() => void exportAs('json')()} testId="tile-export-json-item">
        Export data as JSON…
      </Item>
      <Item why={why} onClick={() => void copyCsv()} testId="tile-copy-csv-item">
        Copy data as CSV
      </Item>
    </>
  );
}
